import { mockParticipants, mockMentors, SOLUTION_TYPES } from '@/data/mockData';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { BadgeDisplay } from '@/components/ui/badge-display';
import { Search, Filter, AlertTriangle, CheckCircle, Clock } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useState } from 'react';

export function MentorParticipantsPage() {
  const mentor = mockMentors[0];
  const myParticipants = mockParticipants.filter(p => mentor.participantIds.includes(p.id));
  const [search, setSearch] = useState('');
  const [selectedParticipant, setSelectedParticipant] = useState(myParticipants[0] || null);

  const filteredParticipants = myParticipants.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.company.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-bold text-foreground">
            Mis Participantes
          </h1>
          <p className="text-muted-foreground mt-1">
            {myParticipants.length} participantes asignados
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* List */}
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Buscar participante..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="space-y-2">
            {filteredParticipants.map((participant) => (
              <button
                key={participant.id}
                onClick={() => setSelectedParticipant(participant)}
                className={`w-full p-4 rounded-xl text-left transition-all ${
                  selectedParticipant?.id === participant.id
                    ? 'bg-primary/10 border-2 border-primary'
                    : 'bg-card border border-border hover:border-primary/50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <img
                    src={participant.avatar}
                    alt={participant.name}
                    className="w-10 h-10 rounded-full"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground truncate">{participant.name}</p>
                    <p className="text-sm text-muted-foreground truncate">{participant.company}</p>
                  </div>
                  {participant.progress < 50 && participant.daysUntilDemoday < 15 ? (
                    <AlertTriangle className="w-5 h-5 text-destructive flex-shrink-0" />
                  ) : participant.canAccessDemoday ? (
                    <CheckCircle className="w-5 h-5 text-success flex-shrink-0" />
                  ) : (
                    <Clock className="w-5 h-5 text-warning flex-shrink-0" />
                  )}
                </div>
                <div className="mt-3">
                  <div className="flex justify-between text-xs text-muted-foreground mb-1">
                    <span>Progreso</span>
                    <span>{participant.progress}%</span>
                  </div>
                  <Progress value={participant.progress} className="h-1.5" />
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Detail */}
        {selectedParticipant && (
          <Card className="glass-card lg:col-span-2">
            <CardHeader>
              <div className="flex items-start gap-4">
                <img
                  src={selectedParticipant.avatar}
                  alt={selectedParticipant.name}
                  className="w-16 h-16 rounded-xl"
                />
                <div className="flex-1">
                  <CardTitle>{selectedParticipant.name}</CardTitle>
                  <p className="text-muted-foreground">{selectedParticipant.company}</p>
                  <BadgeDisplay badges={selectedParticipant.badges} className="mt-2" />
                </div>
                <div className="text-right">
                  <p className="text-3xl font-display font-bold text-foreground">
                    {selectedParticipant.progress}%
                  </p>
                  <p className="text-sm text-muted-foreground">completado</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Challenge */}
              <div className="p-4 rounded-lg bg-muted/50">
                <h4 className="text-sm font-medium text-foreground mb-2">Reto Empresarial</h4>
                <p className="text-sm text-muted-foreground">{selectedParticipant.challenge}</p>
              </div>

              {/* Progress by Solution Type */}
              <div>
                <h4 className="text-sm font-medium text-foreground mb-3">Progreso por Módulo</h4>
                <div className="space-y-3">
                  {([1, 2, 3, 4] as const).map((type) => {
                    const deliverables = selectedParticipant.deliverables.filter(d => d.solutionType === type);
                    const validated = deliverables.filter(d => d.status === 'validated').length;
                    const isUnlocked = type <= selectedParticipant.currentSolutionType;
                    const solution = SOLUTION_TYPES[type];

                    return (
                      <div key={type} className={`flex items-center gap-3 ${!isUnlocked && 'opacity-40'}`}>
                        <span className="text-xl">{solution.icon}</span>
                        <div className="flex-1">
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-foreground">Tipo {type}</span>
                            <span className="text-muted-foreground">
                              {validated}/{deliverables.length || '0'}
                            </span>
                          </div>
                          <Progress 
                            value={deliverables.length ? (validated / deliverables.length) * 100 : 0} 
                            className="h-2" 
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Deliverables */}
              <div>
                <h4 className="text-sm font-medium text-foreground mb-3">Entregables Recientes</h4>
                <div className="space-y-2">
                  {selectedParticipant.deliverables.slice(-3).map((deliverable) => (
                    <div
                      key={deliverable.id}
                      className="flex items-center justify-between p-3 rounded-lg bg-muted/30"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{SOLUTION_TYPES[deliverable.solutionType].icon}</span>
                        <span className="text-sm text-foreground">{deliverable.name}</span>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        deliverable.status === 'validated' ? 'bg-success/20 text-success' :
                        deliverable.status === 'submitted' ? 'bg-info/20 text-info' :
                        'bg-warning/20 text-warning'
                      }`}>
                        {deliverable.status === 'validated' ? 'Validado' :
                         deliverable.status === 'submitted' ? 'Por validar' : 'En progreso'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
